import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

export const Route = createFileRoute('/_authed/community/matches')({
  component: MatchesPage,
});

interface MatchRow {
  metric: string;
  value: number;
  nContributors: number;
  epsilon: number;
  computedAt: number;
}
interface MatchResp {
  cohort: string | null;
  rows: MatchRow[];
  reason?: string;
}

function formatMetric(m: string): string {
  if (m.startsWith('intervention.')) {
    const [, kind] = m.split('.');
    return `${(kind ?? '').replace(/_/g, ' ')} acceptance`;
  }
  if (m.startsWith('bigfive.')) {
    const parts = m.split('.');
    return `${parts[1] ?? ''} mean`;
  }
  return m;
}

function MatchesPage() {
  const { data } = useQuery<MatchResp>({
    queryKey: ['community-matches'],
    queryFn: async () => {
      const r = await fetch('/api/community/matches', { credentials: 'include' });
      if (!r.ok) throw new Error(`matches ${r.status}`);
      return r.json();
    },
  });

  return (
    <section className="mx-auto max-w-2xl px-4 py-12">
      <h1 className="text-2xl font-semibold tracking-tight">peer matches</h1>
      {data?.reason === 'no_consent' && (
        <p className="mt-2 text-sm text-muted-foreground">
          peer matching is off. enable it in /community to see this.
        </p>
      )}
      {data?.reason === 'no_persona' && (
        <p className="mt-2 text-sm text-muted-foreground">
          set a persona during onboarding to be matched to a cohort.
        </p>
      )}
      {data && data.rows.length === 0 && !data.reason && (
        <p className="mt-2 text-sm text-muted-foreground">
          no aggregates available yet for cohort <b>{data.cohort}</b>. the aggregator only publishes when at least 10 people in a cohort have opted in.
        </p>
      )}
      {data && data.rows.length > 0 && (
        <>
          <p className="mt-2 text-sm text-muted-foreground">
            for people whose pattern resembles yours (cohort: <b>{data.cohort}</b>). every number is differentially-private noised. these are not individual peers — never displayed, never reachable.
          </p>
          <ul className="mt-6 space-y-2">
            {data.rows.map((r) => (
              <li key={r.metric} className="rounded-[var(--radius)] border border-border p-3 text-sm">
                <div className="flex items-baseline justify-between">
                  <span>{formatMetric(r.metric)}</span>
                  <span className="font-mono">
                    {r.metric.includes('acceptance')
                      ? `${(r.value * 100).toFixed(0)}%`
                      : r.value.toFixed(2)}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  n={r.nContributors} · ε={r.epsilon.toFixed(2)} · {new Date(r.computedAt).toLocaleDateString()}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
